import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../environment/environment';

@Injectable()
export class ApiService {
  baseUrl: string = environment.apiUrl;
  imageUrl: string = environment.imageUrl;
  private platformId = inject(PLATFORM_ID);

  constructor(private http: HttpClient) {}

  isLoggedIn() {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }
    const loggedIn = localStorage.getItem('token');
    return loggedIn !== undefined && loggedIn !== null && !!loggedIn;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public get<T = any>(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _formData?: any,
    isPublic = false,
    hideLoader = false
  ): Observable<T> {
    const headers = this.buildHeaders(isPublic, hideLoader);
    return this.http.get<T>(this.baseUrl + url, { headers }).pipe(
      map((response) => this.onResponse<T>(response, 'response')),
      catchError((err) => throwError(() => this.onResponse<T>(err, 'error')))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public delete<T = any>(url: string, isPublic = false, hideLoader = false): Observable<T> {
    const headers = this.buildHeaders(isPublic, hideLoader);
    return this.http.delete<T>(this.baseUrl + url, { headers }).pipe(
      map((response) => this.onResponse<T>(response, 'response')),
      catchError((err) => throwError(() => this.onResponse<T>(err, 'error')))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public post<T = any>(
    apiSlug: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formData: any,
    isPublic = false,
    hideLoader = false,
    hasFile?: boolean
  ): Observable<T> {
    const headers = this.buildHeaders(isPublic, hideLoader);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let data: any = formData;

    if (hasFile && formData && !(formData instanceof FormData)) {
      data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        (data as FormData).append(key, value as string | Blob);
      });
    }

    return this.http.post<T>(this.baseUrl + apiSlug, data, { headers }).pipe(
      map((response) => this.onResponse<T>(response, 'response')),
      catchError((err) => throwError(() => this.onResponse<T>(err, 'error')))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public patch<T = any>(
    apiSlug: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formData: any,
    isPublic = false,
    hideLoader = false,
    hasFile?: boolean
  ): Observable<T> {
    const headers = this.buildHeaders(isPublic, hideLoader);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let data: any = formData;

    if (hasFile && formData && !(formData instanceof FormData)) {
      data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        (data as FormData).append(key, value as string | Blob);
      });
    }

    return this.http.patch<T>(this.baseUrl + apiSlug, data, { headers }).pipe(
      map((response) => this.onResponse<T>(response, 'response')),
      catchError((err) => throwError(() => this.onResponse<T>(err, 'error')))
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public put<T = any>(
    apiSlug: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    formData: any,
    isPublic = false,
    hideLoader = false,
    hasFile?: boolean
  ): Observable<T> {
    const headers = this.buildHeaders(isPublic, hideLoader);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let data: any = formData;

    if (hasFile && formData && !(formData instanceof FormData)) {
      data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        (data as FormData).append(key, value as string | Blob);
      });
    }
    return this.http.put<T>(this.baseUrl + apiSlug, data, { headers }).pipe(
      map((response) => this.onResponse<T>(response, 'response')),
      catchError((err) => throwError(() => this.onResponse<T>(err, 'error')))
    );
  }

  private onResponse<T>(response: unknown, type: 'response' | 'error'): T {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const res: any = response;
    if (type === 'response') {
      if (res.status === 403 || res.statusCode === 403) {
        if (isPlatformBrowser(this.platformId)) {
          localStorage.clear();
          window.location.reload();
        }
        return res as T;
      }

      return (res || res.data) as T;
    }

    if (res.status === 403 || res.statusCode === 403) {
      if (isPlatformBrowser(this.platformId)) {
        localStorage.clear();
        window.location.reload();
      }
      return {
        code: res.status,
        status: 'Error',
        message: 'Your session has expired',
      } as unknown as T;
    }

    return (res.error || res) as T;
  }

  // to handle LDM get apis
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public getLDM<T = any>(
    url: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _formData?: any,
    isPublic = false,
    hideLoader = false,
    customToken = false
  ): Observable<T> {
    const headers = this.buildHeaders(isPublic, hideLoader, customToken);
    return this.http.get<T>(this.baseUrl + url, { headers }).pipe(
      map((response) => this.onResponse<T>(response, 'response')),
      catchError((err) => throwError(() => this.onResponse<T>(err, 'error')))
    );
  }

  /**
   * Helper to build custom headers
   */
  // custome header sirf or sirf ldm apis k liye ture hoga ohterwise empty
  private buildHeaders(isPublic: boolean, hideLoader: boolean, customToken?: boolean): HttpHeaders {
    let headers = new HttpHeaders();
    if (isPublic) {
      headers = headers.set('isPublic', 'true');
    }
    if (hideLoader) {
      headers = headers.set('hideLoader', 'true');
    }
    if (customToken) {
      // Custom token (x-access-token)
      headers = headers.set('x-access-token', environment.xAccessToken);
    }
    return headers;
  }
}
